import React, { useState } from "react"
import { Play, ArrowRight } from "lucide-react"
import { Link } from "react-router-dom"

const visualizations = [
  {
    id: 1,
    title: "Solaris Tower",
    type: "Exterior Animation",
    duration: "2:34",
    thumbnail: "https://images.unsplash.com/photo-1431576901776-e539bd916ba2?w=800&q=80&fit=crop",
  },
  {
    id: 2,
    title: "The Meridian",
    type: "Interior Walkthrough",
    duration: "3:18",
    thumbnail: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=800&q=80&fit=crop",
  },
  {
    id: 3,
    title: "Verdant Cultural Centre",
    type: "Aerial Flythrough",
    duration: "1:52",
    thumbnail: "https://images.unsplash.com/photo-1518005020951-eccb494ad742?w=800&q=80&fit=crop",
  },
]

export default function Visualization3D() {
  const [hoveredId, setHoveredId] = useState(null)

  return (
    <section className="py-24 lg:py-32 bg-stone-950">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-end mb-16">
          <div>
            <span className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-4">
              <span className="w-6 h-px bg-amber-400" />
              3D Visualization
            </span>
            <h2 className="font-['Cormorant_Garamond'] text-4xl lg:text-6xl font-light text-stone-100 leading-tight">
              Cinema-grade<br />
              <span className="italic text-stone-400">architectural visuals</span>
            </h2>
          </div>
          <div>
            <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed lg:max-w-sm lg:ml-auto mb-6">
              We produce photorealistic renders, cinematic animations, and immersive virtual tours that communicate design intent with unmatched clarity.
            </p>
            <Link
              to="/works"
              className="inline-flex items-center gap-2 font-['Space_Grotesk'] text-[11px] tracking-[0.2em] text-amber-400 hover:text-amber-300 transition-colors group"
            >
              VIEW ALL VISUALIZATIONS
              <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Video Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {visualizations.map((viz) => (
            <div
              key={viz.id}
              className="group relative overflow-hidden cursor-pointer h-64 lg:h-80"
              onMouseEnter={() => setHoveredId(viz.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <img
                src={viz.thumbnail}
                alt={viz.title}
                className={`w-full h-full object-cover transition-transform duration-700 ${
                  hoveredId === viz.id ? "scale-105" : "scale-100"
                }`}
              />
              {/* Dark overlay */}
              <div className="absolute inset-0 bg-stone-950/40 group-hover:bg-stone-950/60 transition-colors duration-400" />

              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className={`w-14 h-14 border-2 border-stone-200 rounded-full flex items-center justify-center text-stone-200 transition-all duration-300 ${
                    hoveredId === viz.id
                      ? "bg-amber-400 border-amber-400 text-stone-950 scale-110"
                      : ""
                  }`}
                >
                  <Play size={20} fill="currentColor" />
                </div>
              </div>

              {/* Meta info */}
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="flex items-end justify-between">
                  <div>
                    <span className="font-['Space_Grotesk'] text-[9px] tracking-[0.3em] text-amber-400 uppercase block mb-1">
                      {viz.type}
                    </span>
                    <h4 className="font-['Cormorant_Garamond'] text-xl font-light text-stone-100">
                      {viz.title}
                    </h4>
                  </div>
                  <span className="font-['Space_Grotesk'] text-xs text-stone-400 tabular-nums">
                    {viz.duration}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Capabilities */}
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-6 border-t border-stone-800/60 pt-12">
          {[
            { title: "Photorealistic Renders", desc: "Still images with studio-quality lighting and material fidelity" },
            { title: "Cinematic Animations", desc: "Flythrough sequences and drone-perspective reveals" },
            { title: "Interior Walkthroughs", desc: "First-person video tours with architectural accuracy" },
            { title: "VR / 360° Experiences", desc: "Immersive virtual environments for client presentations" },
          ].map((cap) => (
            <div key={cap.title} className="space-y-2">
              <div className="w-1 h-5 bg-amber-400 mb-3" />
              <h4 className="font-['Space_Grotesk'] text-xs text-stone-200 tracking-wide font-medium">
                {cap.title}
              </h4>
              <p className="font-['Inter'] text-xs text-stone-500 leading-relaxed">{cap.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
